import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { COMPANY } from "@/data/content";
import { scrollToSection } from "@/lib/scroll";

const LINES = ["Svets & VVS", "i Storstockholm"];

const lineReveal = {
  hidden: { y: "112%" },
  visible: (i) => ({
    y: "0%",
    transition: { delay: 0.45 + i * 0.16, duration: 0.95, ease: [0.16, 1, 0.3, 1] },
  }),
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.02, 1.14]);

  return (
    <section id="start" ref={ref} data-testid="hero-section" className="relative flex min-h-screen items-end overflow-hidden">
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
        <img
          src="/assets/welding-sparks.webp"
          alt="Rörsvetsning med gnistor – Uusimaa Svets & VVS AB i Storstockholm"
          className="h-full w-full object-cover"
          fetchPriority="high"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-void via-void/50 to-void/25" />
      <div className="absolute inset-0 bg-gradient-to-r from-void/75 via-void/20 to-transparent" />

      <div className="relative z-10 mx-auto w-full max-w-7xl px-4 pb-28 pt-40 sm:px-6 md:pb-20 lg:px-8">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.25, duration: 0.6 }}
          className="mb-8 flex items-center gap-3"
        >
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-weld opacity-70" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-weld" />
          </span>
          <span data-testid="hero-status" className="font-mono text-[11px] uppercase tracking-[0.22em] text-neutral-300">
            Verksam i Storstockholm — Tungelsta
          </span>
        </motion.div>

        <h1 className="font-display text-5xl font-bold uppercase leading-[0.92] tracking-tight text-white sm:text-7xl lg:text-8xl">
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                custom={i}
                variants={lineReveal}
                initial="hidden"
                animate="visible"
                className="block"
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          data-testid="hero-subheading"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.0, duration: 0.7, ease: "easeOut" }}
          className="mt-6 max-w-xl text-base leading-relaxed text-neutral-300 sm:text-lg"
        >
          Vi byter fjärrvärmecentraler och utför noggranna arbeten inom VVS, rör och svetsning – med kvalitet även där
          ingen ser.
        </motion.p>

        <motion.p
          data-testid="hero-motto"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.15, duration: 0.7, ease: "easeOut" }}
          className="mt-5 border-l-2 border-weld pl-4 font-mono text-xs uppercase tracking-[0.18em] text-neutral-400 sm:text-sm"
        >
          ”{COMPANY.motto}”
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3, duration: 0.7, ease: "easeOut" }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            type="button"
            data-testid="hero-cta-contact"
            onClick={() => scrollToSection("#kontakt")}
            className="group flex items-center gap-2 bg-white px-7 py-4 font-mono text-xs uppercase tracking-[0.18em] text-black transition-colors duration-200 hover:bg-neutral-300"
          >
            Kontakta mig
            <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </button>
          <button
            type="button"
            data-testid="hero-cta-projects"
            onClick={() => scrollToSection("#projekt")}
            className="group flex items-center gap-2 border border-white/25 px-7 py-4 font-mono text-xs uppercase tracking-[0.18em] text-white transition-colors duration-200 hover:border-white/70"
          >
            Se tidigare projekt
            <ArrowDown className="h-4 w-4 transition-transform duration-200 group-hover:translate-y-0.5" />
          </button>
        </motion.div>
      </div>
    </section>
  );
}
